// 📁 RunningOvertimeList.jsx — phiên bản chuẩn UI + API báo cáo
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Calendar, Timer, CheckCircle2, FileText, Upload, Trash2, Loader2, Send } from "lucide-react";
import defaultAvatar from "../../../assets/images/avatar/employee-1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faXmark,
  faHourglassHalf,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";

import {
  uploadOvertimeReportFile,
  submitOvertimeReport,
} from "../../../api/hrm/employees/overtimes";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { getRunningOvertimes } from "../../../api/hrm/employees/overtimes";
import { formatDateVN } from "../../../utils/formatUtils";

export default function RunningOvertimeList({
  filters,
  refreshFlag,
  onChanged,
  autoOpenId = null,
  onAutoOpened,
}) {
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = storedUser?.id;

  const runIdRef = useRef(`run_ot_post_${Date.now()}`);
  const reqSeqRef = useRef(0);
  const latestReqIdRef = useRef(0);
  const autoOpenedRef = useRef(null);

  const loadData = async () => {
    if (!filters?.from_date || !filters?.to_date) return;

    const reqId = ++reqSeqRef.current;
    latestReqIdRef.current = reqId;

    setLoading(true);

    // #region agent log fetchData start
    fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "0dfe12",
      },
      body: JSON.stringify({
        sessionId: "0dfe12",
        runId: runIdRef.current,
        hypothesisId: "H3_running_filter_stale_or_unready",
        location: "src/modules/hrm/overtime/RunningOvertimeList.jsx:loadData:start",
        message: "OT running load start",
        data: {
          reqId,
          pageFrom: filters.from_date,
          pageTo: filters.to_date,
          keyword: filters.keyword,
          branch_id: filters.branch_id,
          department_id: filters.department_id,
          status: filters.status,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    try {
      const res = await getRunningOvertimes(filters);
      // #region agent log fetchData response
      fetch("http://127.0.0.1:7317/ingest/a9e47ab1-61a3-4775-b050-0facfebdbb65", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Debug-Session-Id": "0dfe12",
        },
        body: JSON.stringify({
          sessionId: "0dfe12",
          runId: runIdRef.current,
          hypothesisId: "H3_running_filter_stale_or_unready",
          location:
            "src/modules/hrm/overtime/RunningOvertimeList.jsx:loadData:response",
          message: "OT running load response",
          data: {
            reqId,
            latestReqIdAtResponse: latestReqIdRef.current,
            recordsCount: Array.isArray(res) ? res.length : 0,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion

      if (reqId !== latestReqIdRef.current) return;
      setData(res || []);
    } catch (err) {
      console.error("❌ Lỗi load running:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [filters, refreshFlag]); // 👈 load lại nếu đổi filter hoặc refreshFlag

  useEffect(() => {
    if (!autoOpenId || autoOpenedRef.current === autoOpenId) return;
    const target = data.find((item) => String(item.id) === String(autoOpenId));
    if (!target) return;

    autoOpenedRef.current = autoOpenId;
    setSelected({
      ...target,
      report_content: "",
      report_files: [],
    });
    onAutoOpened?.(target);
  }, [autoOpenId, data]);

  if (!loading && data.length === 0) {
    return null;
  }

  // ============================================
  // 🚀 GỬI BÁO CÁO TĂNG CA
  // ============================================
  const handleSubmitReport = async () => {
    if (loading) return;

    try {
      setLoading(true);

      let uploadedFiles = [];

      // 1) Upload từng file (PDF / Ảnh)
      if (selected.report_files?.length > 0) {
        for (const file of selected.report_files) {
          const res = await uploadOvertimeReportFile(file, selected.id); // BE đã làm route upload-file
          uploadedFiles.push({
            file_name: file.name,
            file_url: res.file_url,
          });
        }
      }

      // 2) Gọi API REPORT (PUT JSON)
      await submitOvertimeReport(selected.id, {
        report_content: selected.report_content || "",
        report_files: uploadedFiles,
      });

      showSuccessToast("Đã gửi báo cáo!");

      setSelected(null);
      loadData(); // 🔥 Load lại danh sách
      onChanged?.();
    } catch (err) {
      console.error("Report OT error:", err);
      showErrorToast("Không thể gửi báo cáo!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6">
      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
        Phiếu đang thực hiện – Chờ báo cáo ({data.length} phiếu)
      </h2>

      {/* LIST CARD */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"> */}
      <div className="grid grid-cols-1 mb:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {data.map((item) => {
          const hours = (item.ot_minutes / 60).toFixed(1);
          const isOwner = storedUser?.id === item.employee_id;
          const avatar = item.employee_avatar || defaultAvatar;

          return (
            <div
              key={item.id}
              onClick={() =>
                setSelected({
                  ...item,
                  report_content: "",
                  report_files: [],
                })
              }
              className="group relative bg-white rounded-[1.8rem] p-4 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* ⭐ Badge Trạng thái */}
              {isOwner && (
                <div className="absolute top-0 right-0 bg-emerald-600 text-white px-3 py-1 text-[9px] font-black uppercase tracking-widest rounded-bl-xl shadow-sm">
                  Chờ báo cáo
                </div>
              )}

              {/* ROW 1: Avatar + Info */}
              <div className="flex gap-3 items-center mb-3">
                <div className="relative shrink-0">
                  <img
                    src={avatar}
                    className="w-10 h-10 rounded-xl object-cover border border-gray-50 shadow-sm group-hover:scale-105 transition-transform"
                    alt="avatar"
                  />
                </div>
                <div className="flex flex-col min-w-0">
                  <p className="font-black text-gray-800 text-[13px] leading-tight truncate uppercase tracking-tight">
                    {item.employee_name}
                  </p>
                  <p className="text-[10px] font-bold text-gray-400 italic truncate mt-0.5">
                    {item.branch_name} • {item.department_name}
                  </p>
                </div>
              </div>

              {/* ROW 2: Giờ OT & Ngày (Dàn hàng ngang để giảm chiều cao) */}
              <div className="flex items-center justify-between mb-3 pt-3 border-t border-gray-50">
                <span className="px-2 py-0.5 text-[9px] font-black rounded-lg bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-widest shrink-0">
                  {hours}H OT
                </span>
                <div className="flex items-center text-[11px] font-bold text-gray-500 gap-1.5">
                  <Calendar className="text-gray-300" size={12} />
                  {formatDateVN(item.work_date)}
                </div>
              </div>

              {/* ROW 3: Khung giờ */}
              <div className="flex items-center text-[11px] font-bold text-gray-600 gap-2 mb-3">
                <div className="w-5 h-5 rounded-md bg-slate-50 flex items-center justify-center text-slate-400">
                  <Clock size={10} />
                </div>
                <span className="font-black tracking-tighter">
                  {item.ot_from?.slice(0, 5)} — {item.ot_to?.slice(0, 5)}
                </span>
              </div>

              {/* ROW 4: Lý do */}
              <div className="p-2.5 bg-gray-50/80 rounded-xl border border-gray-100/50 group-hover:bg-white group-hover:border-emerald-100 transition-colors">
                <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed italic font-medium">
                  "{item.reason}"
                </p>
              </div>
            </div>
          );
        })}
      </div>


{/* ✅ POPUP GỬI BÁO CÁO NÂNG CẤP */}
<AnimatePresence>
  {selected && (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setSelected(null)}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />

      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-[2.5rem] shadow-2xl w-[750px] max-w-[95%] overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
      >
        {/* HEADER */}
        <div className="p-8 pb-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-sm border border-emerald-100">
              <CheckCircle2 size={24} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-xl font-black uppercase tracking-tight text-gray-800">Báo cáo tăng ca</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5">Hoàn tất công việc thực tế</p>
            </div>
          </div>
          <button 
            onClick={() => setSelected(null)}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-2xl transition-all border border-gray-100"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* CONTENT */}
        <div className="px-8 pb-8 space-y-6 overflow-y-auto custom-scrollbar">
          {/* Tóm tắt thông tin phiếu */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-slate-50 rounded-[2rem] border border-slate-100">
            <div className="flex items-center gap-4 col-span-1 md:col-span-2 mb-2">
              <img
                src={selected.employee_avatar || defaultAvatar}
                className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-sm"
                alt="avatar"
              />
              <div>
                <div className="text-md font-black text-gray-800 uppercase">{selected.employee_name}</div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {selected.branch_name} • {selected.department_name}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[12px] font-bold text-slate-500">
                <Calendar size={14} className="text-slate-400" /> Ngày: <b className="text-slate-700">{formatDateVN(selected.work_date)}</b>
              </div>
              <div className="flex items-center gap-2 text-[12px] font-bold text-slate-500">
                <Clock size={14} className="text-slate-400" /> Giờ: <b className="text-slate-700">{selected.ot_from?.slice(0, 5)} → {selected.ot_to?.slice(0, 5)}</b>
              </div>
            </div>

            <div className="space-y-2 text-right md:text-left">
              <div className="flex items-center md:justify-start justify-end gap-2 text-[12px] font-bold text-slate-500">
                <Timer size={14} className="text-slate-400" /> Thời lượng: <b className="text-emerald-600">{(selected.ot_minutes / 60).toFixed(2)} giờ</b>
              </div>
              <div className="text-[11px] font-medium text-slate-400 italic leading-tight">
                Lý do: {selected.reason}
              </div>
            </div>
          </div>

          {/* Nhập báo cáo */}
          {currentUserId === selected.employee_id && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2 flex items-center gap-2">
                  <FileText size={14} /> Nội dung báo cáo thực tế *
                </label>
                <textarea
                  className="w-full bg-white border border-gray-100 rounded-[1.5rem] p-5 font-bold text-sm text-gray-700 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-200 transition-all shadow-sm min-h-[120px] placeholder:font-medium placeholder:text-gray-300"
                  placeholder="Mô tả chi tiết những gì bạn đã hoàn thành..."
                  maxLength={250}
                  value={selected.report_content}
                  onChange={(e) => setSelected((prev) => ({ ...prev, report_content: e.target.value }))}
                />
                <div className="text-right text-[10px] font-black text-slate-300 uppercase tracking-widest pr-4">
                  {selected.report_content.length}/250 ký tự
                </div>
              </div>

              {/* TẢI LÊN TÀI LIỆU */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Minh chứng đính kèm</label>
                {selected.report_files.length === 0 ? (
                  <label className="group block border-2 border-dashed border-slate-100 rounded-[1.5rem] p-8 text-center cursor-pointer bg-slate-50 hover:bg-emerald-50 hover:border-emerald-200 transition-all">
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setSelected((prev) => ({ ...prev, report_files: [file] }));
                      }}
                    />
                    <div className="w-12 h-12 rounded-2xl bg-white shadow-sm border border-slate-50 flex items-center justify-center mx-auto mb-3 text-slate-400 group-hover:text-emerald-500 transition-colors">
                      <Upload size={20} />
                    </div>
                    <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest group-hover:text-emerald-600">Chọn hình ảnh hoặc PDF</span>
                    <p className="text-[10px] text-slate-400 mt-1 font-bold italic">Tối đa 25MB • Chỉ chấp nhận 01 tệp</p>
                  </label>
                ) : (
                  (() => {
                    const file = selected.report_files[0];
                    const isImage = file.type.startsWith("image/");
                    return (
                      <div className="bg-white p-4 rounded-3xl border border-emerald-100 flex items-center gap-4 shadow-sm animate-in fade-in slide-in-from-bottom-2">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden border border-gray-50 bg-gray-50 flex items-center justify-center shrink-0">
                          {isImage ? (
                            <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-red-600 font-black text-xs uppercase">PDF</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-black text-gray-700 text-sm truncate">{file.name}</div>
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mt-1">
                            {file.size < 1048576 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / 1048576).toFixed(2)} MB`}
                          </div>
                        </div>
                        <button
                          onClick={() => setSelected((prev) => ({ ...prev, report_files: [] }))}
                          className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors flex items-center justify-center shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-8 border-t border-gray-50 bg-gray-50/50 flex justify-end gap-3">
          <button
            onClick={() => setSelected(null)}
            className="px-6 py-4 rounded-2xl font-black text-[11px] text-gray-400 uppercase tracking-widest hover:bg-gray-100 transition-all"
          >
            Đóng
          </button>
          {currentUserId === selected.employee_id && (
            <button
              disabled={loading}
              onClick={handleSubmitReport}
              className={`px-10 py-4 bg-emerald-600 text-white font-black text-[11px] uppercase tracking-[0.15em] rounded-2xl shadow-lg transition-all active:scale-95 flex items-center gap-2 ${
                loading ? "opacity-60 shadow-none" : "hover:bg-emerald-700 shadow-emerald-100"
              }`}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Đang xử lý..." : "Xác nhận gửi báo cáo"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>
    </div>
  );
}
